import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  usePublishFanPageMutation,
  useSaveFanPageFavoritesMutation,
  useSaveFanPageBattingOrderMutation,
  useSaveFanPageLineupMutation,
} from "../../store/api/mlbApi";
import { BATTING_LEADERS_SLOT } from "../../constants/fanPage";
import { communityBuildPath } from "../../utils/community";
import {
  battingOrderMovePatch,
  battingOrderPlayerIds,
  getFanPagePath,
  getFanPageUrl,
  getLineupPlayerIds,
} from "../../utils/fanPage";
import LineupField from "./LineupField";
import BattingOrderCard from "./BattingOrderCard";
import FavoritesStrip from "./FavoritesStrip";
import PlayerPickerDialog from "./PlayerPickerDialog";
import PublishHandleDialog from "./PublishHandleDialog";

const FanPageScreen = ({
  page,
  loading,
  error,
  onPublished,
  errorMessage = "This lineup isn’t public yet.",
}) => {
  const [picker, setPicker] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const location = useLocation();
  const onPublicPage = location.pathname.startsWith("/u/");
  const [saveLineup] = useSaveFanPageLineupMutation();
  const [saveBattingOrder] = useSaveFanPageBattingOrderMutation();
  const [saveFavorites] = useSaveFanPageFavoritesMutation();
  const [publishPage, { isLoading: publishing }] = usePublishFanPageMutation();

  const editable = Boolean(page?.isOwner);
  const lineupIds = useMemo(
    () => getLineupPlayerIds(page?.lineup),
    [page?.lineup]
  );
  const favoriteIds = page?.favorites?.map((player) => player.playerId) || [];
  const battingIds = useMemo(
    () => battingOrderPlayerIds(page?.battingOrder),
    [page?.battingOrder]
  );

  const handleSaveLineup = async (patch) => {
    try {
      await saveLineup(patch).unwrap();
    } catch (err) {
      setNotice(err?.data?.error || "Could not update lineup");
    }
  };

  const handleSaveBattingOrder = async (patch) => {
    if (!patch) return;
    try {
      await saveBattingOrder(patch).unwrap();
    } catch (err) {
      setNotice(err?.data?.error || "Could not update batting order");
    }
  };

  const handleSaveFavorites = async (nextIds) => {
    try {
      await saveFavorites(nextIds).unwrap();
    } catch (err) {
      setNotice(err?.data?.error || "Could not update favorites");
    }
  };

  const handlePublish = async (handle) => {
    try {
      const result = await publishPage(handle).unwrap();
      setPublishOpen(false);
      setNotice("Your page is live");
      onPublished?.(result.page);
    } catch (err) {
      setNotice(err?.data?.error || "Could not publish");
    }
  };

  const handleCopy = async () => {
    if (!page?.handle) return;
    try {
      await navigator.clipboard.writeText(getFanPageUrl(page.handle));
      setNotice("Link copied");
    } catch (_err) {
      setNotice("Could not copy link");
    }
  };

  const pickerTitle =
    picker?.type === "favorite"
      ? "Add a favorite"
      : picker?.type === "batting"
      ? `Pick your ${picker.hole.pickLabel}`
      : picker?.slot
      ? `Pick your ${picker.slot.label.toLowerCase()}`
      : "Pick a player";

  const excludeIds =
    picker?.type === "lineup"
      ? lineupIds.filter((id) => id !== page?.lineup?.[picker.slot.id]?.playerId)
      : picker?.type === "batting"
      ? battingIds.filter(
          (id) => id !== page?.battingOrder?.[picker.hole.id]?.playerId
        )
      : favoriteIds;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !page) {
    return (
      <Alert severity="info" sx={{ mt: 4 }}>
        {errorMessage}
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.16em" }}
          >
            All-time lineup
          </Typography>
          <Typography variant="h3" component="h1" fontWeight={800} sx={{ mt: 0.5 }}>
            {page.handle ? `@${page.handle}` : "Your nine"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
            {editable
              ? "Tap the field or a batting hole. Publish when you want a public URL."
              : "A personal starting nine — plus the order they’d send to the plate."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {page.published && page.handle && (
            <>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
              >
                Copy link
              </Button>
              <Button
                component={RouterLink}
                to={communityBuildPath("mlb", { versus: `fan:${page.handle}` })}
                variant="contained"
              >
                Challenge this roster
              </Button>
              <Button
                component={RouterLink}
                to={communityBuildPath("mlb", { remix: `fan:${page.handle}` })}
                variant="outlined"
              >
                Make my version
              </Button>
            </>
          )}
          {editable && !page.published && (
            <Button variant="contained" onClick={() => setPublishOpen(true)}>
              Publish page
            </Button>
          )}
          {editable && page.published && page.handle && !onPublicPage && (
            <Button
              component={RouterLink}
              to={getFanPagePath(page.handle)}
              variant="outlined"
            >
              View public page
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.15fr) minmax(320px, 0.9fr)" },
          gap: { xs: 5, lg: 3 },
          alignItems: "start",
        }}
      >
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: "0.16em" }}
          >
            The field
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25 }}>
            Starting nine
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, mb: 2, maxWidth: 520 }}
          >
            {editable ? "Tap a position to put them on the dirt." : "Where they play."}
          </Typography>
          <LineupField
            lineup={page.lineup}
            editable={editable}
            onSelect={(slot) => {
              if (editable) setPicker({ type: "lineup", slot });
            }}
            onClear={(slot) => handleSaveLineup({ [slot.id]: null })}
          />
        </Box>
        <BattingOrderCard
          battingOrder={page.battingOrder}
          editable={editable}
          compact
          onSelect={(hole) => {
            if (editable) setPicker({ type: "batting", hole });
          }}
          onClear={(hole) => handleSaveBattingOrder({ [hole.id]: null })}
          onMove={(hole, delta) =>
            handleSaveBattingOrder(
              battingOrderMovePatch(page.battingOrder, hole.id, delta)
            )
          }
        />
      </Box>

      <Box sx={{ mt: 5 }}>
        <FavoritesStrip
          favorites={page.favorites}
          editable={editable}
          onSelect={() => {}}
          onClear={(player) =>
            handleSaveFavorites(
              favoriteIds.filter((id) => id !== player.playerId)
            )
          }
          onAdd={() => setPicker({ type: "favorite" })}
        />
      </Box>

      <PlayerPickerDialog
        open={Boolean(picker)}
        title={pickerTitle}
        slotId={
          picker?.type === "lineup"
            ? picker.slot?.id
            : picker?.type === "batting"
            ? BATTING_LEADERS_SLOT
            : undefined
        }
        excludePlayerIds={excludeIds}
        onClose={() => setPicker(null)}
        onSelect={(player) => {
          if (picker?.type === "lineup") {
            handleSaveLineup({ [picker.slot.id]: player.playerId });
          } else if (picker?.type === "batting") {
            handleSaveBattingOrder({ [picker.hole.id]: player.playerId });
          } else {
            handleSaveFavorites([...favoriteIds, player.playerId]);
          }
          setPicker(null);
        }}
      />

      <PublishHandleDialog
        open={publishOpen}
        suggestedHandle={page.suggestedHandle}
        publishing={publishing}
        onClose={() => setPublishOpen(false)}
        onPublish={handlePublish}
      />

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice("")}
        message={notice}
      />
    </Box>
  );
};

export default FanPageScreen;
